import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FirmAdminLayout } from './FirmAdminLayout';
import { Bell, Save, Mail, MessageSquare, Smartphone, Clock, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ChannelConfig {
  enabled: boolean;
  frequency?: 'immediate' | 'hourly' | 'daily' | 'weekly' | 'never';
}

interface EventConfig {
  enabled: boolean;
  channels: {
    email?: ChannelConfig;
    sms?: ChannelConfig;
    whatsapp?: ChannelConfig;
    inApp?: ChannelConfig;
    push?: ChannelConfig;
  };
  recipients?: string[];
  customRecipients?: string[];
  delay?: number;
}

interface NotificationConfig {
  _id?: string;
  level: 'tenant' | 'platform';
  tenantId?: string;
  events: Map<string, EventConfig> | Record<string, EventConfig>;
  globalSettings: {
    enabled: boolean;
    quietHoursEnabled?: boolean;
    quietHoursStart?: string;
    quietHoursEnd?: string;
  };
  channelSettings: {
    email: { enabled: boolean };
    sms: { enabled: boolean };
    inApp: { enabled: boolean };
    whatsapp: { enabled: boolean };
    push: { enabled: boolean };
  };
}

// Event categories and types (matching database snake_case)
const EVENT_CATEGORIES = {
  'TASK': [
    { id: 'task_created', label: 'Task Created' },
    { id: 'task_assigned', label: 'Task Assigned to Me' },
    { id: 'task_updated', label: 'Task Updated' },
    { id: 'task_completed', label: 'Task Completed' },
    { id: 'task_overdue', label: 'Task Overdue' },
    { id: 'task_due_soon', label: 'Task Due Soon' },
    { id: 'task_reminder', label: 'Task Reminder' },
  ],
  'TIMESHEET': [
    { id: 'timesheet_submitted', label: 'Timesheet Submitted' },
    { id: 'timesheet_approved', label: 'Timesheet Approved' },
    { id: 'timesheet_rejected', label: 'Timesheet Rejected' },
  ],
  'INVOICE': [
    { id: 'invoice_created', label: 'Invoice Created' },
    { id: 'invoice_generated', label: 'Invoice Generated' },
    { id: 'invoice_sent', label: 'Invoice Sent' },
    { id: 'invoice_paid', label: 'Invoice Paid' },
    { id: 'invoice_overdue', label: 'Invoice Overdue' },
    { id: 'invoice_reminder', label: 'Invoice Reminder' },
  ],
  'LEAVE': [
    { id: 'leave_applied', label: 'Leave Applied' },
    { id: 'leave_approved', label: 'Leave Approved' },
    { id: 'leave_rejected', label: 'Leave Rejected' },
    { id: 'leave_cancelled', label: 'Leave Cancelled' },
  ],
  'USER': [
    { id: 'user_created', label: 'User Created' },
    { id: 'user_invited', label: 'User Invited' },
    { id: 'user_updated', label: 'User Updated' },
    { id: 'password_reset', label: 'Password Reset' },
    { id: 'password_changed', label: 'Password Changed' },
  ],
  'ATTENDANCE': [
    { id: 'attendance_clock_in', label: 'Attendance Clock In' },
    { id: 'attendance_clock_out', label: 'Attendance Clock Out' },
    { id: 'attendance_missing', label: 'Attendance Missing' },
    { id: 'attendance_overtime', label: 'Attendance Overtime' },
  ],
  'DOCUMENT': [
    { id: 'document_uploaded', label: 'Document Uploaded' },
    { id: 'document_shared', label: 'Document Shared' },
    { id: 'document_expired', label: 'Document Expired' },
    { id: 'document_reminder', label: 'Document Reminder' },
  ],
  'PAYMENT': [
    { id: 'payment_received', label: 'Payment Received' },
    { id: 'payment_failed', label: 'Payment Failed' },
    { id: 'payment_reminder', label: 'Payment Reminder' },
  ],
  'REPORT': [
    { id: 'report_generated', label: 'Report Generated' },
    { id: 'report_scheduled', label: 'Report Scheduled' },
    { id: 'report_failed', label: 'Report Failed' },
  ],
  'SYSTEM': [
    { id: 'system_maintenance', label: 'System Maintenance' },
    { id: 'system_update', label: 'System Update' },
    { id: 'system_backup', label: 'System Backup' },
  ],
  'CLIENT': [
    { id: 'client_created', label: 'Client Created' },
    { id: 'client_updated', label: 'Client Updated' },
    { id: 'client_reminder', label: 'Client Reminder' },
  ],
};

// Tenant Notification Configuration Management
const TenantNotificationConfigManagement: React.FC = () => {
  const { user, tenant } = useAuthStore();
  const [config, setConfig] = useState<NotificationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'events' | 'channels' | 'settings'>('events');
  const [retryCount, setRetryCount] = useState(0);
  const [localCache, setLocalCache] = useState<NotificationConfig | null>(null);

  // Key for localStorage persistence
  const cacheKey = `notification-config-${tenant?.id || 'platform'}`;

  // Load from localStorage on mount
  useEffect(() => {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsedCache = JSON.parse(cached);
        console.log('📦 Loaded notification config from local cache:', parsedCache);
        setLocalCache(parsedCache);
      } catch (err) {
        console.warn('Failed to parse cached config:', err);
      }
    }
    fetchConfig();
  }, []);

  // Save to localStorage whenever config changes
  const saveToLocalCache = (configToCache: NotificationConfig) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(configToCache));
      setLocalCache(configToCache);
      console.log('💾 Saved notification config to local cache');
    } catch (err) {
      console.warn('Failed to save to local cache:', err);
    }
  };

  const fetchConfig = async (isRetry = false) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching notification config...', { isRetry, retryCount });
      const response = await api.get('/notification-config/config/tenant');
      
      console.log('API Response received:', {
        status: response.status,
        hasData: !!response.data,
        success: response.data?.success,
        hasConfigData: !!response.data?.data,
        configData: response.data?.data
      });

      if (response.data.success && response.data.data) {
        const configData = response.data.data;
        // Save successful response to local cache
        saveToLocalCache(configData);
        
        // Ensure channelSettings is properly initialized with ALL 5 channels
        const channelDefaults = {
          email: { enabled: true },
          sms: { enabled: false },
          whatsapp: { enabled: false },
          inApp: { enabled: true },
          push: { enabled: false },
        };
        
        if (!configData.channelSettings) {
          configData.channelSettings = channelDefaults;
          console.log('⚠️ channelSettings was null, initialized with defaults');
        } else {
          // Ensure ALL channel keys exist, add missing ones
          let hasChanges = false;
          Object.keys(channelDefaults).forEach(key => {
            if (!configData.channelSettings[key]) {
              configData.channelSettings[key] = channelDefaults[key];
              console.log(`✅ Added missing channel: ${key}`);
              hasChanges = true;
            }
          });
          if (hasChanges) {
            console.log('📝 Some channels were missing and have been added');
          }
        }
        
        console.log('📋 Final channelSettings after initialization:', {
          email: configData.channelSettings.email,
          sms: configData.channelSettings.sms,
          whatsapp: configData.channelSettings.whatsapp,
          inApp: configData.channelSettings.inApp,
          push: configData.channelSettings.push,
        });

        // Ensure globalSettings exists
        if (!configData.globalSettings) {
          configData.globalSettings = {
            enabled: true,
            quietHoursEnabled: false,
          };
        }

        // Ensure events is a proper object
        if (!configData.events || typeof configData.events === 'undefined') {
          configData.events = {};
        }

        console.log('Config loaded successfully:', {
          channelSettings: configData.channelSettings,
          inAppEnabled: configData.channelSettings?.inApp?.enabled,
          hasEvents: Object.keys(configData.events).length
        });
        
        setConfig(configData);
        setRetryCount(0); // Reset retry count on success
      } else {
        throw new Error('Failed to fetch configuration - invalid response');
      }
    } catch (error: any) {
      console.error('Error fetching config:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load notification configuration';
      setError(errorMessage);
      
      // If API fails, use local cache as fallback
      if (localCache) {
        console.log('⚠️ API failed, using local cache as fallback');
        setConfig(localCache);
      }
      
      // Auto-retry after 2 seconds if this wasn't already a retry
      if (!isRetry && retryCount < 3) {
        console.log(`Retrying in 2 seconds (attempt ${retryCount + 1}/3)...`);
        setTimeout(() => {
          setRetryCount(retryCount + 1);
          fetchConfig(true);
        }, 2000);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEventToggle = (eventId: string, channel?: string) => {
    if (!config) return;

    const updatedConfig = { ...config };
    const events = updatedConfig.events as Record<string, EventConfig>;

    if (!events[eventId]) {
      events[eventId] = {
        enabled: true,
        channels: {
          email: { enabled: true },
          sms: { enabled: false },
          inApp: { enabled: true },
          push: { enabled: false },
        },
      };
    }

    if (channel) {
      const channelKey = channel as keyof EventConfig['channels'];
      if (events[eventId].channels[channelKey]) {
        events[eventId].channels[channelKey]!.enabled = !events[eventId].channels[channelKey]!.enabled;
      }
    } else {
      events[eventId].enabled = !events[eventId].enabled;
    }

    setConfig(updatedConfig);
  };

  const handleChannelToggle = (channel: keyof NotificationConfig['channelSettings']) => {
    console.log('=== handleChannelToggle called ===', {
      channel,
      configExists: !!config,
      currentState: config?.channelSettings?.[channel],
    });

    if (!config) {
      console.error('Config is null!');
      return;
    }

    const updatedConfig = { ...config };
    
    // Ensure channelSettings object exists
    if (!updatedConfig.channelSettings) {
      updatedConfig.channelSettings = {
        email: { enabled: false },
        sms: { enabled: false },
        inApp: { enabled: false },
        whatsapp: { enabled: false },
        push: { enabled: false },
      };
    }

    // Ensure the specific channel exists
    if (!updatedConfig.channelSettings[channel]) {
      updatedConfig.channelSettings[channel] = { enabled: false };
    }

    // Toggle the enabled state
    const oldValue = updatedConfig.channelSettings[channel].enabled;
    updatedConfig.channelSettings[channel].enabled = !oldValue;
    
    const newValue = updatedConfig.channelSettings[channel].enabled;
    
    console.log(`Channel ${channel} toggled:`, {
      oldValue,
      newValue,
      channelConfig: updatedConfig.channelSettings[channel],
      allChannels: updatedConfig.channelSettings,
    });
    
    console.log('Setting config to:', updatedConfig);
    setConfig(updatedConfig);
    
    // Verify state was actually set
    setTimeout(() => {
      console.log('State after setConfig:', {
        channel,
        currentValue: config?.channelSettings?.[channel],
      });
    }, 0);
  };

  const handleGlobalSettingToggle = () => {
    if (!config) return;

    const updatedConfig = { ...config };
    updatedConfig.globalSettings.enabled = !updatedConfig.globalSettings.enabled;
    setConfig(updatedConfig);
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null); // Clear any previous errors
      
      // Convert Map to plain object if needed for events
      let eventsToSend = config.events;
      if (config.events instanceof Map) {
        eventsToSend = Object.fromEntries(config.events);
      } else if (typeof config.events === 'object') {
        eventsToSend = config.events;
      }
      
      // Prepare the config data to send to the backend
      const configToSend = {
        channelSettings: config.channelSettings,
        globalSettings: config.globalSettings,
        events: eventsToSend,
      };

      console.log('Before save - sending to API:', {
        channelSettings: configToSend.channelSettings,
        email: configToSend.channelSettings?.email,
        sms: configToSend.channelSettings?.sms,
        whatsapp: configToSend.channelSettings?.whatsapp,
        inApp: configToSend.channelSettings?.inApp,
        push: configToSend.channelSettings?.push,
        hasEvents: !!configToSend.events,
      });

      const response = await api.put('/notification-config/config/tenant', configToSend);

      if (response.data.success) {
        toast.success('Notification configuration saved successfully');
        
        const responseData = response.data.data;
        console.log('=== SAVE SUCCESS ===');
        console.log('API Response data received:', responseData);
        console.log('Response channelSettings:', responseData?.channelSettings);
        console.log('Response inApp:', responseData?.channelSettings?.inApp);
        
        // CRITICAL: Use response data directly if available, don't fall back to old state
        // The response from the API should contain the updated values from the database
        const mergedConfig: NotificationConfig = {
          ...config,
          ...responseData,
          channelSettings: {
            // Use response data FIRST, then fallback to sent data, then defaults
            email: responseData?.channelSettings?.email ?? configToSend.channelSettings?.email ?? { enabled: true },
            sms: responseData?.channelSettings?.sms ?? configToSend.channelSettings?.sms ?? { enabled: false },
            inApp: responseData?.channelSettings?.inApp ?? configToSend.channelSettings?.inApp ?? { enabled: false },
            whatsapp: responseData?.channelSettings?.whatsapp ?? configToSend.channelSettings?.whatsapp ?? { enabled: false },
            push: responseData?.channelSettings?.push ?? configToSend.channelSettings?.push ?? { enabled: false },
          },
          globalSettings: responseData?.globalSettings ?? configToSend.globalSettings,
          events: responseData?.events ?? configToSend.events,
        };

        console.log('=== MERGED CONFIG ===');
        console.log('Before merge - inApp enabled:', config.channelSettings?.inApp?.enabled);
        console.log('Response - inApp enabled:', responseData?.channelSettings?.inApp?.enabled);
        console.log('After merge - inApp enabled:', mergedConfig.channelSettings.inApp?.enabled);
        console.log('Final merged config:', mergedConfig);
        
        setConfig(mergedConfig);
        // Save to local cache to preserve state even if API fails later
        saveToLocalCache(mergedConfig);
        console.log('✅ Config state updated and cached locally - will NOT auto-refresh from server');

      } else {
        throw new Error(response.data.message || 'Failed to save configuration');
      }
    } catch (error: any) {
      console.error('Error saving config:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save notification configuration';
      setError(errorMessage);
      
      // On save error, still update the config locally to preserve user's changes
      // This ensures the checkbox state persists even if the API call fails
      if (config) {
        console.log('📦 Save failed, but preserving local config state...');
        saveToLocalCache(config);
      }
      
      toast.error(errorMessage);

    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FirmAdminLayout>
        <div className="p-6 flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            <p className="text-gray-600">Loading notification configuration...</p>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  if (error && !config) {
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-full">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-4 text-center">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                  <div>
                    <h3 className="font-semibold text-red-600">Failed to Load Configuration</h3>
                    <p className="text-sm text-gray-600 mt-2">{error}</p>
                  </div>
                  {retryCount < 3 && (
                    <p className="text-xs text-gray-500">Attempting to reconnect... (Attempt {retryCount + 1}/3)</p>
                  )}
                  {retryCount >= 3 && (
                    <Button 
                      onClick={() => {
                        setRetryCount(0);
                        fetchConfig();
                      }}
                      className="w-full mt-4"
                    >
                      Retry Loading Configuration
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  if (!config) {
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600">No configuration available</p>
              <Button 
                onClick={() => fetchConfig()}
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  return (
    <FirmAdminLayout>
      <div className="p-6 space-y-6 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Bell className="h-6 w-6 text-primary-600" />
              <h1 className="text-3xl font-bold">Notification Configuration</h1>
            </div>
            <p className="text-gray-600">
              Manage notification settings and event configurations for your organization
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('events')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'events'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Event Configuration
          </button>
          <button
            onClick={() => setActiveTab('channels')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'channels'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Communication Channels
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Global Settings
          </button>
        </div>

        {/* Event Configuration Tab */}
        {activeTab === 'events' && (
          <div className="space-y-4">
            {Object.entries(EVENT_CATEGORIES).map(([category, events]) => (
              <Card key={category}>
                <CardHeader>
                  <CardTitle className="text-lg">{category} Events</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {events.map((event) => {
                    const eventConfig = (config.events as Record<string, EventConfig>)[event.id];
                    const isEnabled = eventConfig?.enabled ?? true;

                    return (
                      <div key={event.id} className="border rounded-lg p-4 space-y-4">
                        {/* Event Enable/Disable */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => handleEventToggle(event.id)}
                              className="w-4 h-4 rounded text-primary-600"
                            />
                            <span className="font-medium text-gray-900">{event.label}</span>
                          </div>
                          {isEnabled && <CheckCircle className="h-5 w-5 text-green-500" />}
                        </div>

                        {isEnabled && (
                          <div className="ml-7 space-y-4">
                            {/* Communication Channels */}
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Send via:</p>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                {[
                                  { id: 'email', label: 'Email', icon: Mail },
                                  { id: 'sms', label: 'SMS', icon: MessageSquare },
                                  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
                                  { id: 'inApp', label: 'In-App', icon: Bell },
                                  { id: 'push', label: 'Push', icon: Smartphone },
                                ].map(({ id, label, icon: Icon }) => {
                                  const channelKey = id as keyof typeof eventConfig.channels;
                                  const channelConfig = eventConfig?.channels?.[channelKey];
                                  return (
                                    <label key={id} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-gray-50 border border-gray-200">
                                      <input
                                        type="checkbox"
                                        checked={channelConfig?.enabled ?? false}
                                        onChange={() => {
                                          const updatedConfig = { ...config };
                                          const events = updatedConfig.events as Record<string, EventConfig>;
                                          if (!events[event.id].channels[channelKey]) {
                                            events[event.id].channels[channelKey] = { enabled: false, frequency: 'immediate' };
                                          }
                                          const ch = events[event.id].channels[channelKey];
                                          if (ch) ch.enabled = !ch.enabled;
                                          setConfig(updatedConfig);
                                        }}
                                        className="w-4 h-4 rounded text-primary-600"
                                      />
                                      <Icon className="w-4 h-4 text-gray-600" />
                                      <span className="text-sm text-gray-700">{label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Channel Frequencies */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700">Frequency per Channel:</Label>
                              {[
                                { id: 'email', label: 'Email' },
                                { id: 'sms', label: 'SMS' },
                                { id: 'whatsapp', label: 'WhatsApp' },
                                { id: 'inApp', label: 'In-App' },
                                { id: 'push', label: 'Push' },
                              ].map(({ id, label }) => {
                                const channelKey = id as keyof typeof eventConfig.channels;
                                const channelConfig = eventConfig?.channels?.[channelKey];
                                if (!channelConfig?.enabled) return null;
                                
                                return (
                                  <div key={id} className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 w-16">{label}:</span>
                                    <select
                                      value={channelConfig?.frequency ?? 'immediate'}
                                      onChange={(e) => {
                                        const updatedConfig = { ...config };
                                        const events = updatedConfig.events as Record<string, EventConfig>;
                                        if (events[event.id].channels[channelKey]) {
                                          events[event.id].channels[channelKey]!.frequency = e.target.value as any;
                                        }
                                        setConfig(updatedConfig);
                                      }}
                                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                    >
                                      <option value="immediate">Immediately</option>
                                      <option value="hourly">Hourly</option>
                                      <option value="daily">Daily</option>
                                      <option value="weekly">Weekly</option>
                                    </select>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Recipient Types */}
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Notify:</p>
                              <div className="grid grid-cols-2 gap-3">
                                {[
                                  { id: 'assignee', label: 'Event Assignee' },
                                  { id: 'manager', label: 'Manager' },
                                  { id: 'firm_admin', label: 'Firm Admin' },
                                  { id: 'all_staff', label: 'All Staff' },
                                ].map(({ id, label }) => (
                                  <label key={id} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                                    <input
                                      type="checkbox"
                                      checked={eventConfig?.recipients?.includes(id as any) ?? false}
                                      onChange={(e) => {
                                        const updatedConfig = { ...config };
                                        const events = updatedConfig.events as Record<string, EventConfig>;
                                        if (!events[event.id].recipients) {
                                          events[event.id].recipients = [];
                                        }
                                        if (e.target.checked) {
                                          events[event.id].recipients!.push(id as any);
                                        } else {
                                          events[event.id].recipients = events[event.id].recipients!.filter(r => r !== id);
                                        }
                                        setConfig(updatedConfig);
                                      }}
                                      className="w-4 h-4 rounded text-primary-600"
                                    />
                                    <span className="text-sm text-gray-700">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Delay */}
                            <div>
                              <Label>Delay before sending (minutes)</Label>
                              <input
                                type="number"
                                min="0"
                                max="1440"
                                value={eventConfig?.delay ?? 0}
                                onChange={(e) => {
                                  const updatedConfig = { ...config };
                                  const events = updatedConfig.events as Record<string, EventConfig>;
                                  if (events[event.id]) {
                                    events[event.id].delay = parseInt(e.target.value) || 0;
                                  }
                                  setConfig(updatedConfig);
                                }}
                                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
                                placeholder="0"
                              />
                              <p className="text-xs text-gray-500 mt-1">Leave at 0 to send immediately</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Communication Channels Tab */}
        {activeTab === 'channels' && (
          <Card>
            <CardHeader>
              <CardTitle>Communication Channel Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                { id: 'email', label: 'Email Notifications', icon: Mail, description: 'Send notifications via email' },
                { id: 'sms', label: 'SMS Notifications', icon: MessageSquare, description: 'Send notifications via SMS' },
                { id: 'whatsapp', label: 'WhatsApp Notifications', icon: MessageSquare, description: 'Send notifications via WhatsApp' },
                { id: 'inApp', label: 'In-App Notifications', icon: Bell, description: 'Show notifications in application' },
                { id: 'push', label: 'Push Notifications', icon: Smartphone, description: 'Send push notifications to devices' },
              ].map(({ id, label, icon: Icon, description }) => {
                const channelKey = id as keyof typeof config.channelSettings;
                const channelConfig = config?.channelSettings?.[channelKey];
                const isEnabled = channelConfig?.enabled === true;
                
                // Debug logging
                console.log(`Rendering ${id} checkbox:`, {
                  isEnabled,
                  channelConfig,
                  enabled_value: channelConfig?.enabled,
                  type_check: typeof channelConfig?.enabled,
                });
                
                const handleClick = (e: React.MouseEvent) => {
                  console.log(`Clicked ${id} checkbox, isEnabled before:`, isEnabled);
                  e.stopPropagation();
                  handleChannelToggle(channelKey);
                };
                
                const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  console.log(`${id} checkbox onChange fired, checked:`, e.target.checked);
                  e.stopPropagation();
                  handleChannelToggle(channelKey);
                };
                
                return (
                  <div 
                    key={id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <div 
                      className="flex items-center space-x-4 flex-1 cursor-pointer"
                      onClick={handleClick}
                    >
                      <Icon className="w-6 h-6 text-gray-600" />
                      <div>
                        <p className="font-medium text-gray-900">{label}</p>
                        <p className="text-sm text-gray-600">{description}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isEnabled === true}
                      onChange={handleCheckboxChange}
                      onClick={(e) => {
                        console.log(`${id} checkbox click handler triggered`);
                        e.stopPropagation();
                      }}
                      disabled={false}
                      className="w-6 h-6 rounded text-primary-600 cursor-pointer flex-shrink-0 accent-green-600"
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Global Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Global Notification Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Master Enable/Disable */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <Bell className="w-6 h-6 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">Enable All Notifications</p>
                      <p className="text-sm text-gray-600">Master switch to enable/disable all notifications</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.globalSettings.enabled}
                    onChange={handleGlobalSettingToggle}
                    className="w-6 h-6 rounded text-primary-600 cursor-pointer"
                  />
                </div>

                {/* Quiet Hours */}
                {config.globalSettings.enabled && (
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-gray-600" />
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.globalSettings.quietHoursEnabled ?? false}
                          onChange={(e) => {
                            const updatedConfig = { ...config };
                            updatedConfig.globalSettings.quietHoursEnabled = e.target.checked;
                            setConfig(updatedConfig);
                          }}
                          className="w-4 h-4 rounded text-primary-600"
                        />
                        <span className="font-medium text-gray-900">Enable Quiet Hours</span>
                      </label>
                    </div>

                    {config.globalSettings.quietHoursEnabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Quiet Hours Start</Label>
                          <input
                            type="time"
                            value={config.globalSettings.quietHoursStart ?? '22:00'}
                            onChange={(e) => {
                              const updatedConfig = { ...config };
                              updatedConfig.globalSettings.quietHoursStart = e.target.value;
                              setConfig(updatedConfig);
                            }}
                            className="w-full mt-2 px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <Label>Quiet Hours End</Label>
                          <input
                            type="time"
                            value={config.globalSettings.quietHoursEnd ?? '08:00'}
                            onChange={(e) => {
                              const updatedConfig = { ...config };
                              updatedConfig.globalSettings.quietHoursEnd = e.target.value;
                              setConfig(updatedConfig);
                            }}
                            className="w-full mt-2 px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={fetchConfig}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </FirmAdminLayout>
  );
};

export default TenantNotificationConfigManagement;
